import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Save, Loader2, ArrowLeft, Eye, Pencil, CheckCircle2 } from "lucide-react";
import { createVisitor } from "@/lib/api";
import { formatHariTanggalID } from "@/lib/format";
import { toast } from "sonner";

const EMPTY = {
  tanggal_kunjungan: "", jumlah_tamu: 1,
  nama_tamu: "", jabatan_tamu: "",
  instansi_asal: "", perihal_kunjungan: "", whatsapp_tamu: "",
};
const REQUIRED = ["tanggal_kunjungan", "nama_tamu", "jabatan_tamu", "instansi_asal", "perihal_kunjungan"];

export default function BukuTamu() {
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState("form"); // form | preview | success
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const goPreview = (e) => {
    e.preventDefault();
    for (const k of REQUIRED) {
      if (!String(form[k] ?? "").trim()) {
        toast.error("Mohon lengkapi semua kolom bertanda *");
        return;
      }
    }
    setStep("preview");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        jumlah_tamu: Number(form.jumlah_tamu) || 1,
        source: "buku_tamu",
      };
      await createVisitor(payload);
      toast.success("Kunjungan tercatat di Buku Tamu");
      setStep("success");
    } catch {
      toast.error("Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const Row = ({ label, value }) => (
    <div className="flex gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="w-40 text-xs uppercase tracking-wider font-semibold text-slate-500 pt-0.5">{label}</div>
      <div className="flex-1 text-sm text-slate-900 font-medium">{value || "—"}</div>
    </div>
  );

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="buku-tamu-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C59B27] font-semibold">
          <BookOpen className="w-4 h-4" /> Buku Tamu
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">Catat Kunjungan Anda</h1>
        <p className="text-slate-600 text-sm mt-1 max-w-2xl">
          Isi 6 kolom singkat berikut. Setelah menekan "Lihat Ringkasan", Anda bisa memeriksa data sebelum mengirim.
        </p>
      </div>

      {step === "form" && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">Formulir Buku Tamu</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={goPreview} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Tanggal Bertamu <span className="text-red-500">*</span>
                </Label>
                <Input type="date" value={form.tanggal_kunjungan}
                       onChange={(e) => update("tanggal_kunjungan", e.target.value)}
                       className="bg-white border-slate-300" data-testid="buku-tanggal" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Jumlah Tamu (Orang) <span className="text-red-500">*</span>
                </Label>
                <Input type="number" min="1" value={form.jumlah_tamu}
                       onChange={(e) => update("jumlah_tamu", e.target.value)}
                       className="bg-white border-slate-300" data-testid="buku-jumlah" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Nama Tamu <span className="text-red-500">*</span>
                </Label>
                <Input value={form.nama_tamu} onChange={(e) => update("nama_tamu", e.target.value)}
                       className="bg-white border-slate-300" data-testid="buku-nama" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Jabatan Tamu <span className="text-red-500">*</span>
                </Label>
                <Input value={form.jabatan_tamu} onChange={(e) => update("jabatan_tamu", e.target.value)}
                       className="bg-white border-slate-300" data-testid="buku-jabatan" />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Instansi Asal Tamu <span className="text-red-500">*</span>
                </Label>
                <Input value={form.instansi_asal} onChange={(e) => update("instansi_asal", e.target.value)}
                       className="bg-white border-slate-300" data-testid="buku-instansi" />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Nomor WhatsApp Tamu <span className="text-slate-400 normal-case">(opsional)</span>
                </Label>
                <Input type="tel" value={form.whatsapp_tamu}
                       onChange={(e) => update("whatsapp_tamu", e.target.value)}
                       placeholder="08xxxxxxxxxx"
                       className="bg-white border-slate-300" data-testid="buku-whatsapp" />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                  Tujuan Bertamu <span className="text-red-500">*</span>
                </Label>
                <Textarea value={form.perihal_kunjungan}
                          onChange={(e) => update("perihal_kunjungan", e.target.value)}
                          className="min-h-20 bg-white border-slate-300" data-testid="buku-tujuan" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <Button type="submit" className="btn-primary" data-testid="buku-preview-button">
                  <Eye className="w-4 h-4 mr-2" /> Lihat Ringkasan
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {step === "preview" && (
        <Card className="border-[#C59B27] border-2 shadow-lg">
          <CardHeader className="border-b border-amber-100 bg-amber-50/60">
            <CardTitle className="text-base font-semibold text-[#0B2545] flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#C59B27]" /> Ringkasan — Periksa Sebelum Dikirim
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="rounded-md bg-slate-50/70 border border-slate-200 px-5 py-3 mb-4">
              <Row label="Tanggal Bertamu" value={formatHariTanggalID(form.tanggal_kunjungan)} />
              <Row label="Jumlah Tamu" value={`${form.jumlah_tamu} orang`} />
              <Row label="Nama Tamu" value={form.nama_tamu} />
              <Row label="Jabatan Tamu" value={form.jabatan_tamu} />
              <Row label="Instansi Asal" value={form.instansi_asal} />
              <Row label="WhatsApp Tamu" value={form.whatsapp_tamu} />
              <Row label="Tujuan Bertamu" value={form.perihal_kunjungan} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep("form")} disabled={saving} data-testid="buku-edit-button">
                <Pencil className="w-4 h-4 mr-2" /> Kembali Edit
              </Button>
              <Button onClick={submit} disabled={saving} className="btn-primary" data-testid="buku-submit-button">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Kirim ke Buku Tamu
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "success" && (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-8 text-center">
            <div className="inline-flex w-16 h-16 rounded-full bg-emerald-500 text-white items-center justify-center shadow-md">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-slate-900">Terima Kasih Atas Kunjungan Anda</h2>
            <p className="text-slate-600 mt-1">Data kunjungan telah tercatat di Buku Tamu Sekretariat DPRD Kabupaten Seruyan.</p>
            <div className="mt-5 flex justify-center gap-2">
              <Button onClick={() => { setForm(EMPTY); setStep("form"); }} className="btn-primary" data-testid="buku-new-entry">
                <BookOpen className="w-4 h-4 mr-2" /> Catat Kunjungan Lain
              </Button>
              <Button variant="outline" onClick={() => nav("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Kembali ke Beranda
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
